import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { getRefreshToken, clearSession } from '../lib/auth.js';
import { maskPhone } from '../lib/format.js';
import { useSetupState, type SetupStep, type WizardGroup } from '../lib/setupState.js';
import { BrandMark } from './Brand.js';
import { Icon } from './Icon.js';
import { PairingModal } from './PairingModal.js';

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Guided onboarding WIZARD — the first-broadcast path for a NON-technical business owner.
// Full-screen (no sidebar/app chrome). Five plain-Hebrew steps with a progress rail. EVERY choice is
// persisted through the REAL API (createAccount / setSyncGroup / addTargetFromGroup), so a refresh
// resumes exactly where the customer left off (state is derived by useSetupState, never faked).
// The app itself stays LOCKED behind this wizard (AppShell redirects here) until setup is complete.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const STEP_LABELS: Record<SetupStep, string> = {
  1: 'ברוכים הבאים',
  2: 'חיבור מספר',
  3: 'קבוצת שליטה',
  4: 'קבוצות יעד',
  5: 'מתי לשלוח?',
  6: 'מוכנים!',
};
const STEP_ORDER: SetupStep[] = [1, 2, 3, 4, 5, 6];

export function OnboardingWizard(): JSX.Element {
  const setup = useSetupState();
  const navigate = useNavigate();
  const [step, setStep] = useState<SetupStep | null>(null);

  // Resume at the earliest step that still needs the customer (from real, persisted data).
  useEffect(() => {
    if (!setup.loading && step === null) setStep(setup.initialStep);
  }, [setup.loading, setup.initialStep, step]);

  async function logout(): Promise<void> {
    await api.logout(getRefreshToken());
    clearSession();
    window.location.assign('/login');
  }

  if (setup.loading || step === null) {
    return (
      <div className="wiz-screen">
        <div className="wiz-loading"><span className="wiz-spinner" /> טוען…</div>
      </div>
    );
  }

  return (
    <div className="wiz-screen">
      <div className="wiz-shell">
        <header className="wiz-top">
          <div className="wiz-brand"><BrandMark /><span>Cross<b>Castr</b></span></div>
          <button className="wiz-logout" onClick={logout} type="button">התנתקות</button>
        </header>

        <ProgressRail current={step} />

        <main className="wiz-card">
          {step === 1 && <StepWelcome onNext={() => setStep(2)} />}
          {step === 2 && <StepConnect setup={setup} onNext={() => setStep(3)} />}
          {step === 3 && <StepControl setup={setup} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
          {step === 4 && <StepTargets setup={setup} onBack={() => setStep(3)} onNext={() => setStep(5)} />}
          {step === 5 && <StepTiming onBack={() => setStep(4)} onNext={() => setStep(6)} />}
          {step === 6 && <StepReady setup={setup} onEnter={() => navigate('/')} />}
        </main>
      </div>
    </div>
  );
}

// ─── progress rail ────────────────────────────────────────────────────────────────────────────
function ProgressRail({ current }: { current: SetupStep }): JSX.Element {
  return (
    <div className="wiz-rail" role="progressbar" aria-valuemin={1} aria-valuemax={6} aria-valuenow={current}>
      {STEP_ORDER.map((s) => {
        const state = s < current ? 'done' : s === current ? 'now' : 'todo';
        return (
          <div key={s} className={`wiz-node ${state}`}>
            <span className="wiz-dot">{state === 'done' ? <Icon name="check" /> : s}</span>
            <span className="wiz-node-label">{STEP_LABELS[s]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1 — welcome ───────────────────────────────────────────────────────────────────────
function StepWelcome({ onNext }: { onNext: () => void }): JSX.Element {
  return (
    <div className="wiz-step wiz-center">
      <div className="wiz-hero-emoji">📣</div>
      <h1 className="wiz-h1">שלום, וברוכים הבאים ל‑CrossCastr</h1>
      <p className="wiz-lead">
        שולחים הודעה <b>אחת</b> — והיא מגיעה לכל הקבוצות שלכם, אוטומטית. בלי להעתיק ולהדביק, בלי לפספס אף קבוצה.
      </p>
      <p className="wiz-sub">בשלוש דקות נחבר אתכם ותהיו מוכנים לשידור הראשון. יאללה 👇</p>
      <button className="btn wiz-cta" onClick={onNext}>בואו נתחיל <Icon name="send" /></button>
    </div>
  );
}

// ─── Step 2 — connect a sending number ─────────────────────────────────────────────────────────
function StepConnect({ setup, onNext }: { setup: ReturnType<typeof useSetupState>; onNext: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [modal, setModal] = useState<null | { mode: 'new' } | { mode: 'existing'; id: string; label: string }>(null);

  const connected = setup.connectedAccount;
  // A number exists but isn't connected yet (mid-pairing) — offer to reopen its code.
  const pending = setup.accounts.find((a) => a.status !== 'connected') ?? null;
  const closeModal = (): void => { setModal(null); void qc.invalidateQueries({ queryKey: ['accounts'] }); };

  return (
    <div className="wiz-step">
      <StepHead
        eyebrow="שלב 1"
        title="חברו את המספר ששולח"
        sub="זה מספר הוואטסאפ שממנו יישלחו ההודעות לקבוצות. נפיק לכם קוד בן 8 ספרות שתקלידו בוואטסאפ בטלפון — וזהו."
      />

      {connected ? (
        <div className="wiz-success-card">
          <div className="wiz-check-badge"><Icon name="check" /></div>
          <div>
            <div className="wiz-success-title">המספר מחובר!</div>
            <div className="wiz-success-sub ltr">{connected.phoneNumber ? maskPhone(connected.phoneNumber) : connected.label}</div>
          </div>
        </div>
      ) : pending ? (
        <div className="wiz-info-card">
          <span className="wiz-spinner" /> מתחברים… הקלידו את הקוד בוואטסאפ שבטלפון כדי לסיים.
          <button className="btn ghost wiz-inline-btn" onClick={() => setModal({ mode: 'existing', id: pending.id, label: pending.label })}>
            <Icon name="phone" /> הצג קוד חיבור
          </button>
        </div>
      ) : (
        <button className="btn wiz-cta" onClick={() => setModal({ mode: 'new' })}>
          <Icon name="phone" /> חברו מספר וואטסאפ
        </button>
      )}

      <WizFooter next={{ label: 'המשך', onClick: onNext, disabled: !connected }} />

      {modal?.mode === 'new' && <PairingModal mode="new" onClose={closeModal} />}
      {modal?.mode === 'existing' && (
        <PairingModal mode="existing" accountId={modal.id} label={modal.label} onClose={closeModal} />
      )}
    </div>
  );
}

// ─── Step 3 — choose the CONTROL (sync) group ──────────────────────────────────────────────────
function StepControl({ setup, onBack, onNext }: { setup: ReturnType<typeof useSetupState>; onBack: () => void; onNext: () => void }): JSX.Element {
  const qc = useQueryClient();
  const accountId = setup.connectedAccount?.id ?? '';
  const [choice, setChoice] = useState('');
  const [adminPhone, setAdminPhone] = useState('');

  const effective = choice || setup.syncGroupJid || '';

  const save = useMutation({
    mutationFn: () => api.setSyncGroup(accountId, { groupJid: effective, adminPhone: adminPhone.trim() || undefined }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['groups', accountId] });
      onNext();
    },
  });

  const waiting = setup.requiresSession || setup.groups.length === 0;

  return (
    <div className="wiz-step">
      <StepHead
        eyebrow="שלב 2"
        title="בחרו את קבוצת השליטה"
        sub="זו הקבוצה שבה תכתבו מהטלפון. כשתכתבו בה הודעה — נשדר אותה אוטומטית לכל קבוצות היעד. (בדרך כלל קבוצה פרטית קטנה שרק אתם בה.)"
      />

      {waiting ? (
        <div className="wiz-info-card"><span className="wiz-spinner" /> טוענים את הקבוצות שלכם מהוואטסאפ… רגע אחד 😊</div>
      ) : (
        <>
          <label className="wiz-label">קבוצת השליטה</label>
          <select className="wiz-select" value={effective} onChange={(e) => setChoice(e.target.value)}>
            <option value="">— בחרו קבוצה —</option>
            {setup.groups.map((g) => <option key={g.jid} value={g.jid}>{g.name}</option>)}
          </select>

          <label className="wiz-label" style={{ marginTop: 14 }}>המספר האישי שלכם <span className="wiz-opt">(לא חובה)</span></label>
          <div className="wiz-hint">המספר שממנו תכתבו בקבוצת השליטה — כדי שנזהה שזה אתם. אפשר גם לדלג.</div>
          <input className="wiz-input ltr" dir="ltr" placeholder="+972 54-000-0000" value={adminPhone} onChange={(e) => setAdminPhone(e.target.value)} />
        </>
      )}

      <WizFooter
        back={onBack}
        next={{ label: save.isPending ? 'שומר…' : 'המשך', onClick: () => save.mutate(), disabled: !effective || save.isPending || waiting }}
      />
    </div>
  );
}

// ─── Step 4 — pick TARGET groups ───────────────────────────────────────────────────────────────
function StepTargets({ setup, onBack, onNext }: { setup: ReturnType<typeof useSetupState>; onBack: () => void; onNext: () => void }): JSX.Element {
  const qc = useQueryClient();
  const accountId = setup.connectedAccount?.id ?? '';
  const [suffix, setSuffix] = useState('🔗 הצטרפו אלינו ←');
  const [busyJid, setBusyJid] = useState<string | null>(null);
  const [quotaMsg, setQuotaMsg] = useState<string | null>(null);

  // maxTargets is the plan quota (dashboard.kpis.groupQuota === tenant.maxTargets) — respect it.
  const { data: dash } = useQuery({ queryKey: ['dashboard', 30], queryFn: () => api.dashboard(30) });
  const quota = dash?.kpis.groupQuota ?? null;

  const selected = useMemo(() => new Set(setup.selectedJids), [setup.selectedJids]);
  const targetGroups = setup.groups.filter((g) => g.jid !== setup.syncGroupJid);
  const atQuota = quota !== null && selected.size >= quota;

  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: ['groups', accountId] }).then(() => qc.invalidateQueries({ queryKey: ['targets'] }));

  const toggle = useMutation({
    mutationFn: async (g: WizardGroup) => {
      setBusyJid(g.jid);
      setQuotaMsg(null);
      if (selected.has(g.jid)) {
        await api.removeTargetFromGroup({ groupJid: g.jid });
      } else {
        await api.addTargetFromGroup({ waAccountId: accountId, groupJid: g.jid, name: g.name, memberCount: g.memberCount, suffix: suffix.trim() || undefined });
      }
    },
    onError: (err) => {
      setQuotaMsg(String(err).includes('target_quota_exceeded')
        ? `הגעתם למספר הקבוצות המרבי בתוכנית שלכם${quota ? ` (${quota})` : ''}. אפשר להסיר קבוצה או לשדרג תוכנית.`
        : 'לא הצלחנו לשמור את הקבוצה. נסו שוב.');
    },
    onSettled: async () => { await invalidate(); setBusyJid(null); },
  });

  const waiting = setup.requiresSession || setup.groups.length === 0;

  return (
    <div className="wiz-step">
      <StepHead
        eyebrow="שלב 3"
        title="לאן לשדר?"
        sub="סמנו את הקבוצות שאליהן ההודעה תגיע. לכל הודעה נוסיף בסוף את הטקסט הקבוע שלמטה (למשל קישור להצטרפות)."
      />

      {waiting ? (
        <div className="wiz-info-card"><span className="wiz-spinner" /> טוענים את הקבוצות שלכם… רגע אחד 😊</div>
      ) : (
        <>
          <label className="wiz-label">טקסט קבוע בסוף כל הודעה <span className="wiz-opt">(לא חובה)</span></label>
          <input className="wiz-input" value={suffix} onChange={(e) => setSuffix(e.target.value)} placeholder="למשל: קישור להצטרפות לערוץ" />

          <div className="wiz-count">
            נבחרו <b>{selected.size}</b> קבוצות{quota !== null ? <> מתוך {quota}</> : null}
          </div>
          {quotaMsg && <div className="wiz-warn">{quotaMsg}</div>}

          <div className="wiz-grouplist">
            {targetGroups.map((g) => {
              const on = selected.has(g.jid);
              const disabled = (toggle.isPending && busyJid === g.jid) || (!on && atQuota);
              return (
                <label key={g.jid} className={`wiz-group ${on ? 'on' : ''}`} style={disabled && !on ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
                  <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggle.mutate(g)} />
                  <span className="wiz-group-name">{g.name}</span>
                  <span className="wiz-group-count">{g.memberCount} חברים</span>
                </label>
              );
            })}
          </div>
        </>
      )}

      <WizFooter
        back={onBack}
        next={{ label: 'כמעט סיימנו', onClick: onNext, disabled: selected.size === 0 }}
      />
    </div>
  );
}

// ─── Step 5 — WHEN to send ("מתי לשלוח?") ───────────────────────────────────────────────────────
// The user, not the system, decides when broadcasts leave. Two plain choices, saved to the REAL
// tenant settings (api.updateSettings → TenantSettings.sendWindow), which every new campaign copies
// onto its pacing window. Immediate = 0/24 (never held); quiet-hours = the [start,end) they pick.
function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}
function StepTiming({ onBack, onNext }: { onBack: () => void; onNext: () => void }): JSX.Element {
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });
  const [mode, setMode] = useState<'immediate' | 'quiet' | null>(null);
  const [start, setStart] = useState(8);
  const [end, setEnd] = useState(22); // default SUGGESTION 08:00–22:00 (the user can change both)

  // Seed the controls once from real saved settings. An untouched tenant (8–21 default) keeps the
  // friendlier 08–22 suggestion; an explicit immediate/custom window is adopted as-is.
  useEffect(() => {
    if (!settings || mode !== null) return;
    const { start: s, end: e } = settings.sendWindow;
    if (s === 0 && e === 24) setMode('immediate');
    else if (s === 8 && e === 21) setMode('quiet');
    else { setMode('quiet'); setStart(s); setEnd(e); }
  }, [settings, mode]);

  const save = useMutation({
    mutationFn: () => api.updateSettings({ sendWindow: mode === 'immediate' ? { start: 0, end: 24 } : { start, end } }),
    onSuccess: () => onNext(),
  });

  const startOpts = Array.from({ length: 24 }, (_, i) => i); // 0..23
  const endOpts = Array.from({ length: 24 - start }, (_, i) => start + 1 + i); // (start+1)..24
  function changeStart(v: number): void { setStart(v); if (end <= v) setEnd(Math.min(v + 1, 24)); }

  return (
    <div className="wiz-step">
      <StepHead
        eyebrow="שלב 4"
        title="מתי לשלוח את ההודעות?"
        sub="אתם מחליטים מתי הודעה יוצאת לקבוצות — עכשיו אפשר להגדיר את זה פעם אחת (תמיד ניתן לשנות בהגדרות)."
      />

      <button
        type="button"
        className={`wiz-group ${mode === 'immediate' ? 'on' : ''}`}
        style={{ display: 'block', textAlign: 'start', padding: 16, marginBottom: 10 }}
        onClick={() => setMode('immediate')}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>⚡ שליחה מיידית</div>
        <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>כל הודעה נשלחת מיד ברגע שאתם שולחים אותה — בכל שעה.</div>
      </button>

      <button
        type="button"
        className={`wiz-group ${mode === 'quiet' ? 'on' : ''}`}
        style={{ display: 'block', textAlign: 'start', padding: 16, marginBottom: 10 }}
        onClick={() => setMode('quiet')}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>🌙 רק בשעות שתבחרו</div>
        <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>הודעה שנכתבה אחרי השעה שקבעתם תמתין ותישלח אוטומטית בבוקר.</div>
      </button>

      {mode === 'quiet' && (
        <div className="wiz-info-card" style={{ display: 'block', marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 15 }}>
            <span>שלחו בין השעה</span>
            <select className="wiz-select" style={{ width: 110 }} value={start} onChange={(e) => changeStart(Number(e.target.value))}>
              {startOpts.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
            <span>לשעה</span>
            <select className="wiz-select" style={{ width: 110 }} value={end} onChange={(e) => setEnd(Number(e.target.value))}>
              {endOpts.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </div>
          <div className="wiz-hint" style={{ marginTop: 10 }}>
            הודעה שתישלח אחרי {hourLabel(end)} תמתין ותצא למחרת בשעה {hourLabel(start)}.
          </div>
        </div>
      )}

      <WizFooter
        back={onBack}
        next={{
          label: save.isPending ? 'שומר…' : 'המשך',
          onClick: () => save.mutate(),
          disabled: mode === null || save.isPending,
        }}
      />
    </div>
  );
}

// ─── Step 6 — ready ────────────────────────────────────────────────────────────────────────────
function StepReady({ setup, onEnter }: { setup: ReturnType<typeof useSetupState>; onEnter: () => void }): JSX.Element {
  const controlName = setup.groups.find((g) => g.jid === setup.syncGroupJid)?.name ?? 'קבוצת השליטה';
  const acct = setup.connectedAccount;
  const phone = acct?.phoneNumber ? maskPhone(acct.phoneNumber) : (acct?.label ?? '');
  // Show the tenant's REAL current broadcast keyword in the phone instruction (default 'שידור').
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });
  const keyword = settings?.broadcastKeyword ?? 'שידור';

  return (
    <div className="wiz-step wiz-center">
      <div className="wiz-confetti">🎉</div>
      <h1 className="wiz-h1">מעולה — אתם מוכנים לשדר!</h1>

      <div className="wiz-recap">
        <RecapRow icon="phone" label="מספר שולח" value={phone} />
        <RecapRow icon="bolt" label="קבוצת שליטה" value={controlName} />
        <RecapRow icon="wa" label="קבוצות יעד" value={`${setup.selectedJids.length} קבוצות`} />
      </div>

      <div className="wiz-ways">
        <div className="wiz-way">
          <div className="wiz-way-num">1</div>
          <div><b>כותבים כאן באתר</b><br /><span>מקלידים הודעה ולוחצים "שלח" — מגיע לכל הקבוצות.</span></div>
        </div>
        <div className="wiz-way">
          <div className="wiz-way-num">2</div>
          <div>
            <b>כותבים בקבוצת השליטה מהטלפון</b><br />
            <span>
              כתבו בקבוצת השליטה: <b>"{keyword} ההודעה שלכם"</b> — ואנחנו נשדר אותה לבד לכל היעדים. את מילת השידור אפשר לשנות בכל עת בהגדרות.
            </span>
          </div>
        </div>
      </div>

      <button className="btn wiz-cta" onClick={onEnter}>כניסה למערכת <Icon name="send" /></button>
    </div>
  );
}

// ─── small shared pieces ────────────────────────────────────────────────────────────────────────
function StepHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub: string }): JSX.Element {
  return (
    <div className="wiz-head">
      <div className="wiz-eyebrow">{eyebrow}</div>
      <h2 className="wiz-h2">{title}</h2>
      <p className="wiz-headsub">{sub}</p>
    </div>
  );
}

function RecapRow({ icon, label, value }: { icon: 'phone' | 'bolt' | 'wa'; label: string; value: string }): JSX.Element {
  return (
    <div className="wiz-recap-row">
      <span className="wiz-recap-ic"><Icon name={icon} /></span>
      <span className="wiz-recap-label">{label}</span>
      <span className="wiz-recap-value">{value}</span>
    </div>
  );
}

function WizFooter({ back, next }: { back?: () => void; next: { label: string; onClick: () => void; disabled?: boolean } }): JSX.Element {
  return (
    <div className="wiz-foot">
      {back ? <button className="wiz-back" onClick={back} type="button">→ חזרה</button> : <span />}
      <button className="btn wiz-cta" onClick={next.onClick} disabled={next.disabled}>{next.label}</button>
    </div>
  );
}
